import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { lessonNoteLink, lessonNoteParts, safeLessonNoteHref } from "@wmf/shared/notes";
import { helpTopics, type HelpTopicSummary } from "@wmf/shared/help";
import { get } from "./api.js";
import { slashQuery } from "./helpSlashQuery.js";
export { slashQuery } from "./helpSlashQuery.js";

export interface LessonResourceMatch {
  kind: string;
  id: string;
  title: string;
  typeLabel: string;
  sourceLabel?: string;
  repertoireId?: string;
}

export type LessonResourceContext = { context: "private"; bookingId: string }
  | { context: "school"; sessionId: string; pupilId: string };

interface ResourceResults {
  resources: LessonResourceMatch[]; hasMore: boolean; nextPage?: number | null; nextCursor?: string | null; topics?: HelpTopicSummary[];
}

function readEditor(root: HTMLElement): string {
  function read(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
    if (!(node instanceof HTMLElement)) return "";
    const href = node.tagName === "A" ? node.getAttribute("href") : null;
    if (href && safeLessonNoteHref(href)) return lessonNoteLink(node.textContent ?? "", href);
    if (node.tagName === "BR") return "\n";
    const text = [...node.childNodes].map(read).join("");
    return node !== root && /^(DIV|P)$/.test(node.tagName) ? `${text}\n` : text;
  }
  return read(root);
}

function drawEditor(root: HTMLElement, content: string) {
  root.replaceChildren(...lessonNoteParts(content).map((part) => {
    if (!part.href) return document.createTextNode(part.text);
    const link = document.createElement("a");
    link.href = part.href;
    link.textContent = part.text;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.contentEditable = "false";
    return link;
  }));
}

function selectionOffset(root: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !selection.isCollapsed || !root.contains(selection.anchorNode)) return null;
  const range = selection.getRangeAt(0).cloneRange();
  range.selectNodeContents(root);
  range.setEnd(selection.anchorNode!, selection.anchorOffset);
  return range.toString().length;
}

function rangeAt(root: HTMLElement, start: number, end = start): Range {
  const range = document.createRange();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
  if (nodes.length === 0) { const node = document.createTextNode(""); root.append(node); nodes.push(node); }
  function point(offset: number): [Text, number] {
    for (const node of nodes) {
      if (offset <= node.length) return [node, offset];
      offset -= node.length;
    }
    const last = nodes[nodes.length - 1]!;
    return [last, last.length];
  }
  range.setStart(...point(start));
  range.setEnd(...point(end));
  return range;
}

/** Links remain readable and clickable while composing; storage stays plain text. */
export function LessonResourceEditor({ id, value, onChange, resourceContext, onAdd, onBusyChange, disabled = false, describedBy }: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  resourceContext: LessonResourceContext;
  onAdd: (resource: LessonResourceMatch, idempotencyKey: string) => Promise<{ title: string; href: string }>;
  onBusyChange?: (busy: boolean) => void;
  disabled?: boolean;
  describedBy?: string;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const lastValue = useRef<string>();
  const contextKey = JSON.stringify(resourceContext);
  const activeContext = useRef(contextKey);
  activeContext.current = contextKey;
  const mounted = useRef(true);
  const listId = useId();
  const [trigger, setTrigger] = useState<ReturnType<typeof slashQuery>>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [results, setResults] = useState<(ResourceResults & { key: string }) | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [active, setActive] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [retry, setRetry] = useState(0);
  const operation = useRef<{ resource: string; key: string } | null>(null);
  const searchKey = trigger ? `${contextKey}:${trigger.start}:${trigger.kind ?? "resources"}:${trigger.topic ?? ""}:${trigger.browseTopics ? "topics" : trigger.query}` : null;
  const dismissKey = trigger?.browseTopics ? `${searchKey}:${trigger.topicQuery}` : searchKey;
  const activeSearch = useRef(searchKey);
  activeSearch.current = searchKey;
  const open = searchKey !== null && dismissKey !== dismissed && !disabled;
  const current = results?.key === searchKey ? results : null;
  const options: LessonResourceMatch[] = trigger?.browseTopics ? (current?.topics ?? []).filter(topic => {
    const search = (trigger.topicQuery ?? "").toLowerCase().replace(/-/g, " ");
    const definition = helpTopics.find(value => value.id === topic.id);
    return definition && [topic.id, topic.label, ...definition.aliases].some(value => value.toLowerCase().replace(/-/g, " ").includes(search));
  }).map(topic => ({ kind: "help_topic", id: topic.id, title: topic.label, typeLabel: `${topic.count} guide${topic.count === 1 ? "" : "s"}`, sourceLabel: `/help/${topic.id}` }))
    : current?.resources ?? [];

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    setAdding(false); setTrigger(null); setDismissed(null); operation.current = null;
  }, [contextKey]);
  useEffect(() => {
    if (open && options[active]) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, listId, open, results, trigger?.topicQuery]);
  useEffect(() => { if (trigger?.browseTopics) setActive(0); }, [trigger?.browseTopics, trigger?.topicQuery]);

  useEffect(() => {
    if (editor.current && value !== lastValue.current) {
      drawEditor(editor.current, value);
      lastValue.current = value;
      setTrigger(null);
    }
  }, [value]);

  useEffect(() => {
    setResults(null);
    setLoadingMore(false);
    setActive(0);
    setProblem(null);
    if (!open || !trigger || !searchKey) return;
    let live = true;
    const timer = window.setTimeout(() => {
      void get<ResourceResults>("/lesson-resources/search", {
        ...JSON.parse(contextKey) as Record<string, string>, q: trigger.query, ...(trigger.kind ? { kind: trigger.kind, includeTopics: trigger.browseTopics ? "1" : "0" } : {}),
        ...(trigger.topic ? { topic: trigger.topic } : {}),
      }).then((result) => { if (live) setResults({ ...result, key: searchKey }); })
        .catch((cause: unknown) => { if (live) setProblem(cause instanceof Error ? cause.message : "Could not search resources."); });
    }, 250);
    return () => { live = false; window.clearTimeout(timer); };
  }, [contextKey, searchKey, open, retry]);

  async function loadMore(selectNext = false) {
    if (!current?.hasMore || !trigger || loadingMore || !searchKey) return;
    const firstNew = options.length;
    setLoadingMore(true); setProblem(null);
    try {
      const next = await get<ResourceResults>("/lesson-resources/search", {
        ...JSON.parse(contextKey) as Record<string, string>, q: trigger.query, ...(trigger.kind ? { kind: trigger.kind, includeTopics: "0" } : {}),
        ...(trigger.topic ? { topic: trigger.topic } : {}),
        ...(current.nextCursor ? { cursor: current.nextCursor } : { page: String(current.nextPage ?? 2) }),
      });
      if (!mounted.current || activeSearch.current !== searchKey) return;
      setResults((previous) => previous?.key !== searchKey ? previous : { ...next, key: searchKey, resources: [...previous.resources, ...next.resources] });
      if (selectNext && next.resources.length) setActive(firstNew);
    } catch (cause) {
      if (mounted.current && activeSearch.current === searchKey) setProblem(cause instanceof Error ? cause.message : "Could not load more matches.");
    } finally { if (mounted.current && activeSearch.current === searchKey) setLoadingMore(false); }
  }

  function refreshTrigger() {
    const root = editor.current;
    if (!root) return;
    const offset = selectionOffset(root);
    setTrigger(offset === null ? null : slashQuery(root.textContent ?? "", offset));
  }

  function changed() {
    if (!editor.current) return;
    const next = readEditor(editor.current);
    lastValue.current = next;
    onChange(next);
    refreshTrigger();
  }

  function insertText(text: string) {
    if (disabled || adding) return;
    const root = editor.current;
    const selection = window.getSelection();
    if (!root || !selection?.rangeCount || !root.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges(); selection.addRange(range);
    changed();
  }

  async function choose(resource: LessonResourceMatch) {
    const root = editor.current;
    if (!root || !trigger || adding) return;
    if (resource.kind === "help_topic") {
      const range = rangeAt(root, trigger.start, trigger.end);
      range.deleteContents();
      const text = document.createTextNode(`/help/${resource.id} `);
      range.insertNode(text); range.setStartAfter(text); range.collapse(true); root.focus();
      const cursor = window.getSelection(); cursor?.removeAllRanges(); cursor?.addRange(range);
      changed(); return;
    }
    const selection = { ...trigger };
    const resourceKey = `${contextKey}:${resource.kind}:${resource.id}`;
    if (operation.current?.resource !== resourceKey) operation.current = { resource: resourceKey, key: crypto.randomUUID() };
    setAdding(true); onBusyChange?.(true); setProblem(null);
    try {
      const added = await onAdd(resource, operation.current.key);
      if (!mounted.current || activeContext.current !== contextKey || editor.current !== root) return;
      if (!safeLessonNoteHref(added.href)) throw new Error("The resource did not return a valid lesson link.");
      const range = rangeAt(root, selection.start, selection.end);
      range.deleteContents();
      const link = document.createElement("a");
      link.href = added.href; link.textContent = added.title;
      link.target = "_blank"; link.rel = "noreferrer"; link.contentEditable = "false";
      const space = document.createTextNode(" ");
      range.insertNode(space); range.insertNode(link);
      range.setStartAfter(space); range.collapse(true);
      root.focus();
      const cursor = window.getSelection(); cursor?.removeAllRanges(); cursor?.addRange(range);
      changed(); setTrigger(null); operation.current = null;
    } catch (cause) {
      if (mounted.current && activeContext.current === contextKey) setProblem(cause instanceof Error ? cause.message : "Could not add that resource. Try again.");
    } finally {
      if (mounted.current && activeContext.current === contextKey) { setAdding(false); onBusyChange?.(false); }
    }
  }

  function keyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.nativeEvent.isComposing) return;
    if (disabled || adding) { if (event.key === "Enter") event.preventDefault(); return; }
    if (open && event.key === "Escape") { event.preventDefault(); setDismissed(dismissKey); return; }
    if (open && options.length > 0 && ["Tab", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const backward = event.key === "ArrowUp" || (event.key === "Tab" && event.shiftKey);
      if (!backward && active === options.length - 1 && current?.hasMore && !trigger?.browseTopics) { void loadMore(true); return; }
      setActive((index) => (index + (backward ? -1 : 1) + options.length) % options.length);
      return;
    }
    if (open && event.key === "Enter") {
      event.preventDefault();
      if (options[active]) void choose(options[active]);
      return;
    }
    if (event.key === "Enter") { event.preventDefault(); insertText("\n"); }
  }

  return <div className="lesson-resource-editor">
    <div ref={editor} id={id} role="textbox" aria-label="Notes and practice" aria-multiline="true"
      aria-describedby={describedBy} aria-autocomplete="list" aria-controls={open ? listId : undefined}
      aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
      aria-disabled={disabled || adding} contentEditable={!disabled && !adding} suppressContentEditableWarning
      className="lesson-resource-text" data-placeholder="What did you work on? What should they practise before the next lesson?"
      onInput={changed} onKeyDown={keyDown} onKeyUp={refreshTrigger} onClick={refreshTrigger}
      onBlur={(event) => { if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node)) setTrigger(null); }}
      onBeforeInput={(event) => { if ((event.nativeEvent as InputEvent).inputType === "insertParagraph") { event.preventDefault(); insertText("\n"); } }}
      onPaste={(event) => { event.preventDefault(); insertText(event.clipboardData.getData("text/plain")); }}
      onDrop={(event) => event.preventDefault()} />
    <p className="small muted">Type / and a resource name, /help to browse topics, or /help/practice for practice guides. Tab or arrows choose; Enter selects; Escape closes.</p>
    {open && <div className="lesson-resource-menu">
      <div id={listId} role="listbox" aria-label={trigger?.browseTopics ? "Help topics" : trigger?.kind === "help" ? "Matching help guides" : "Matching lesson resources"}>
        {options.map((resource, index) => <button type="button" role="option" id={`${listId}-${index}`}
          key={`${resource.kind}:${resource.id}`} aria-selected={index === active} disabled={adding}
          onMouseDown={(event) => event.preventDefault()} onClick={() => void choose(resource)}>
          <span>{resource.title}</span><span className="small muted">{resource.typeLabel}{resource.sourceLabel ? ` · ${resource.sourceLabel}` : ""}</span>
        </button>)}
      </div>
      <p className="small" role="status">{adding ? "Adding resource…" : problem ? problem : loadingMore ? "Loading more matches…" : !current ? "Searching…" : options.length === 0 ? trigger?.browseTopics ? "No matching topics with published student or parent guides." : trigger?.kind === "help" ? "No published student or parent guides match. Try another word." : "No matching resources." : `${options.length} ${trigger?.browseTopics ? "topic" : `matching ${trigger?.kind === "help" ? "guide" : "resource"}`}${options.length === 1 ? "" : "s"}${current.hasMore && !trigger?.browseTopics ? " shown" : ""}`}</p>
      {current?.hasMore && !trigger?.browseTopics && <button type="button" className="quiet" disabled={loadingMore || adding} onMouseDown={(event) => event.preventDefault()} onClick={() => void loadMore()}>{loadingMore ? "Loading…" : "Load more matches"}</button>}
      {problem && <button type="button" className="quiet" onClick={() => setRetry((version) => version + 1)}>Retry search</button>}
    </div>}
  </div>;
}
